import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { UserKeys, getDriveTransferLogs } from '../../api/users-api';
import { DataTransferLog, Page } from '../../../types';
import { Messages } from '../../utils/message-constants';
import { createGridOverlays } from '../grid-overlay/no-data-overlay-helpers';
import { GridColDef } from '@mui/x-data-grid-pro';
import { OwnershipTransferLogsGridToolbar } from './ownership-transfer-logs-grid-toolbar';
import { useUserSettings } from '../../utils/use-user-settings';
import SharedDataGridClient from '../../shared/shared-data-grid-client';
import StatusLabel, { StatusType } from '../../shared/status-label';
import FindInPage from '@mui/icons-material/FindInPage';

export default function OwnershipTransferLogsPage() {
  const { viewType } = useUserSettings();
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
  }>({
    pageSize: 25,
    page: 0,
    cursor: null,
  });

  const {
    data: driveTransferLogData,
    isLoading,
    isFetching,
  } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      UserKeys.DriveTransferLogs,
      paginationModel.page,
      paginationModel.pageSize,
    ],
    queryFn: () => {
      const object: Page<DataTransferLog[]> = {
        results: [],
        firstPull: paginationModel.page === 0,
        searchTime: 0,
        pageSize: paginationModel.pageSize,
        cursor: paginationModel.cursor,
        nameSpace: '',
      };

      return getDriveTransferLogs(object, object.pageSize);
    },
    placeholderData: keepPreviousData,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('ownership transfer logs'),
    },
  });

  const columns: GridColDef<DataTransferLog>[] = [
    {
      field: 'dateCreated',
      headerName: 'Date Created',
      editable: false,
      flex: 1,
      type: 'dateTime',
      valueGetter: (value) => {
        if (value === null || value === undefined) return null;
        return new Date(value as number);
      },
      valueFormatter: (params: Date | null) => {
        if (!params) return '';
        return params.toLocaleString();
      },
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      editable: false,
      flex: 1,
    },
    {
      field: 'srcUser',
      headerName: 'Source User',
      editable: false,
      flex: 1,
    },
    {
      field: 'destUser',
      headerName: 'Destination User',
      editable: false,
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Status',
      editable: false,
      flex: 1,
      width: 120,
      renderCell: (params) => {
        const rawStatus = params.row.status || 'INPROGRESS';
        const status = rawStatus
          .replace(/\s+/g, '')
          .toUpperCase() as StatusType;
        return <StatusLabel status={status} />;
      },
    },
  ];

  const defaultGridState = {
    sorting: {
      sortModel: [
        {
          field: 'dateCreated',
          sort: 'desc',
        },
      ],
    },
  };

  return (
    <>
      <SharedDataGridClient
        paginationModel={paginationModel}
        slots={{
          toolbar: OwnershipTransferLogsGridToolbar,
          ...createGridOverlays('Logs', <FindInPage />),
        }}
        defaultGridState={defaultGridState}
        gridKey={
          viewType.ownershipTransferLogsGridStates?.activeGridState || 'default'
        }
        gridStateKey="ownershipTransferLogsGridStates"
        columns={columns}
        rows={driveTransferLogData?.results || []}
        getRowId={(row) => row.transferId}
        loading={isLoading || isFetching}
        onPaginationModelChange={(newModel) => {
          if (isFetching || isLoading) return;

          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel({
              page: 0,
              pageSize: newModel.pageSize,
              cursor: null,
            });
          } else {
            setPaginationModel({
              ...newModel,
              cursor: driveTransferLogData?.cursor || '',
            });
          }
        }}
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'default',
          },
        }}
      />
    </>
  );
}
